/**
 * Pinned Messages Service
 * API calls for pinning/unpinning messages in channels
 */

import apiClient from './api.client';
import type { ApiResponse, MessageReaction, MessageAttachment } from '@/types/api.types';

export interface PinnedMessage {
  id: string;
  message_id: string;
  channel_id: string;
  pinned_by: string;
  pinned_at: string;
  messages: {
    id: string;
    content: string;
    user_id: string;
    channel_id: string;
    created_at: string;
    is_edited: boolean;
    users: {
      id: string;
      email: string;
      display_name: string;
      avatar_url: string | null;
    };
    message_reactions?: MessageReaction[];
    message_attachments?: MessageAttachment[];
  };
  users: {
    id: string;
    email: string;
    display_name: string;
  };
}

/**
 * Pin a message to a channel
 */
export const pinMessage = async (
  channelId: string,
  messageId: string
): Promise<PinnedMessage> => {
  const response = await apiClient.post<ApiResponse<{ pin: PinnedMessage }>>(
    `/channels/${channelId}/messages/${messageId}/pin`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to pin message');
  }

  return response.data.data.pin;
};

/**
 * Unpin a message from a channel
 */
export const unpinMessage = async (
  channelId: string,
  messageId: string
): Promise<void> => {
  const response = await apiClient.delete<ApiResponse>(
    `/channels/${channelId}/messages/${messageId}/pin`
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to unpin message');
  }
};

/**
 * Get all pinned messages for a channel
 */
export const getPinnedMessages = async (
  channelId: string
): Promise<PinnedMessage[]> => {
  const response = await apiClient.get<
    ApiResponse<{ pinnedMessages: PinnedMessage[]; count: number }>
  >(`/channels/${channelId}/pinned`);

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch pinned messages');
  }

  return response.data.data.pinnedMessages;
};
